/**
 * Silences the benign "ResizeObserver loop completed with undelivered
 * notifications." error.
 *
 * rsuite bundles the `@juggle/resize-observer` ponyfill. When it detects a
 * resize loop it does NOT throw — it synthesises an `ErrorEvent('error')` and
 * dispatches it on `window` (see @juggle/resize-observer's
 * `deliverResizeLoopError`). The message is harmless, but Create React App's
 * dev error overlay treats every window `error` event as a fatal runtime
 * error and shows the full-screen red overlay.
 *
 * We intercept `window.dispatchEvent` and silently drop ONLY that specific
 * synthetic event. Since the event is never dispatched, no listener — the CRA
 * overlay included — ever sees it. Every other event passes through untouched.
 *
 * This module is imported first in `index.tsx`, so the patch is installed
 * before any rsuite component mounts and triggers the observer.
 */
const RESIZE_OBSERVER_MSG = 'ResizeObserver loop'

if (typeof window !== 'undefined') {
  // 1. Source-level: stop @juggle/resize-observer's synthetic error event.
  if (typeof window.dispatchEvent === 'function') {
    const nativeDispatchEvent = window.dispatchEvent.bind(window)
    window.dispatchEvent = (event: Event): boolean => {
      if (
        event instanceof ErrorEvent &&
        typeof event.message === 'string' &&
        event.message.includes(RESIZE_OBSERVER_MSG)
      ) {
        return true
      }
      return nativeDispatchEvent(event)
    }
  }

  // 2. Safety net: swallow any ResizeObserver-loop error that still reaches
  //    the normal window 'error' channel (e.g. the browser-native observer).
  window.addEventListener(
    'error',
    (e) => {
      if (e.message && e.message.includes(RESIZE_OBSERVER_MSG)) {
        e.stopImmediatePropagation()
        e.preventDefault()
      }
    },
    true
  )
}

export {}
